/**
 * @fileoverview Checkbox list for selecting AI extracts as key points
 */

const SENTIMENT_COLORS = {
  positive: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
  negative: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  mixed: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200',
};

export default function KeyPointSelector({
  extracts = [],
  selectedPoints = [],
  onSelectionChange,
  disabled = false,
  maxDisplay = 100,
}) {
  if (!extracts || extracts.length === 0) {
    return null;
  }

  const handleToggle = (extractText) => {
    if (disabled) return;

    const newSelection = selectedPoints.includes(extractText)
      ? selectedPoints.filter((p) => p !== extractText)
      : [...selectedPoints, extractText];

    onSelectionChange(newSelection);
  };

  const truncateText = (text, maxLength = maxDisplay) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Select key points to emphasize:</p>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {extracts.map((extract, index) => {
          const extractText = typeof extract === 'string' ? extract : extract.text || extract.extract;
          const sentiment = typeof extract === 'object' ? extract.sentiment : null;
          const isSelected = selectedPoints.includes(extractText);

          return (
            <label
              key={index}
              className={`flex items-start gap-2 text-xs p-1.5 rounded cursor-pointer transition-colors ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              } ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(extractText)}
                disabled={disabled}
                className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed"
              />
              <span className="text-gray-700 dark:text-gray-200 flex-1">{truncateText(extractText)}</span>
              {sentiment && (
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                    SENTIMENT_COLORS[sentiment] || SENTIMENT_COLORS.neutral
                  }`}
                >
                  {sentiment}
                </span>
              )}
            </label>
          );
        })}
      </div>
      {selectedPoints.length > 0 && (
        <p className="text-[10px] text-indigo-600 dark:text-indigo-400 mt-1">
          {selectedPoints.length} point{selectedPoints.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
}
